import { Box, formatTimestamp, HistoryTrackingCard, LogoLoader } from "@bcc-ui/ui-components";
import React, { JSX, useEffect } from "react";
import { useTranslation } from "react-i18next";

import style from "./asset-side-panel.scss";
import { resolveAssetPathKey } from "domain/asset-management/assetPathMapping";
import { AssetEvent, assetService } from "services/asset-management/AssetService";
import { createTranslatorPath } from "services/pathTranslator";
import { isStringBlank, isStringNotBlank } from "utils/commonFunctions";
import { logger } from "utils/logging";

interface Props {
    assetUuid: string;
}
export function AssetHistoryView(props: Props): JSX.Element {
    const { t } = useTranslation();
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");

    const [loading, setLoading] = React.useState(false);
    const [events, setEvents] = React.useState<AssetEvent[]>([]);
    const [creationDate, setCreationDate] = React.useState<string>();
    const [error, setError] = React.useState<string>();
    useEffect(() => {
        setLoading(true);
        const abortController = new AbortController();
        assetService
            .fetchAssetEvents(props.assetUuid, abortController)
            .then((response) => {
                if (!abortController.signal.aborted) {
                    setEvents(response.events);
                    setCreationDate(response.creationDate);
                }
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Error occurred while fetching asset events.", error);
                    setError(t("AssetManagement.sidePanel.history.errorMessage"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => abortController.abort();
    }, [props.assetUuid]);

    function skipEvent(event: AssetEvent) {
        return (
            isStringBlank(event.keyBefore) &&
            isStringBlank(event.valueBefore) &&
            isStringBlank(event.keyAfter) &&
            isStringBlank(event.valueAfter)
        );
    }
    function generatePrimaryContent(event: AssetEvent) {
        const resolvedKeyAfter = resolveAssetPathKey(event.keyAfter);
        const keyAfterLabel = resolvedKeyAfter ? translatedPaths(resolvedKeyAfter) : event.keyAfter;

        if (event.valueBefore !== event.valueAfter && isStringNotBlank(event.keyAfter)) {
            if (isStringBlank(event.valueBefore)) {
                return t("AssetManagement.sidePanel.history.primaryContentForNewValue", {
                    key: keyAfterLabel,
                    valueAfter: event.valueAfter,
                });
            }
            return t("AssetManagement.sidePanel.history.primaryContentForValue", {
                key: keyAfterLabel,
                valueBefore: event.valueBefore,
                valueAfter: event.valueAfter,
            });
        }

        const resolvedKeyBefore = resolveAssetPathKey(event.keyBefore);
        return t("AssetManagement.sidePanel.history.primaryContentForKey", {
            keyBefore: resolvedKeyBefore ? translatedPaths(resolvedKeyBefore) : event.keyBefore,
            keyAfter: keyAfterLabel,
        });
    }
    function generateSecondaryContent(event: AssetEvent) {
        return t("AssetManagement.sidePanel.history.secondaryContent", {
            editedBy: event.editedBy,
            editedDate: formatTimestamp(event.eventDate),
        });
    }

    function createHistoryTracker() {
        return error ? (
            <div>{error}</div>
        ) : (
            <div>
                {events.map(
                    (each, index) =>
                        !skipEvent(each) && (
                            <HistoryTrackingCard
                                key={index}
                                primaryContent={generatePrimaryContent(each)}
                                secondaryContent={generateSecondaryContent(each)}
                            />
                        )
                )}
                {creationDate && (
                    <HistoryTrackingCard
                        key={creationDate}
                        primaryContent={t("AssetManagement.sidePanel.history.creationDate", {
                            creationDate: formatTimestamp(creationDate),
                        })}
                        secondaryContent={null}
                    />
                )}
            </div>
        );
    }
    return loading ? (
        <Box horizontalAlignment="Center" verticalAlignment="Center" className={style.loadingState}>
            <LogoLoader />
        </Box>
    ) : (
        createHistoryTracker()
    );
}
